// Third party imports
import { createSlice } from "@reduxjs/toolkit";

// Copies of the company the social links are edited on: the sidebar only when it is the
// same company (it stays mounted across clients)
const linkCopies = (state) =>
  [
    state.selectedCompany,
    !state.selectedCompany || state.sidebar?.id === state.selectedCompany.id ? state.sidebar : null,
  ].filter(Boolean);

export const companiesSlice = createSlice({
  name: "companies",
  initialState: {
    companies: [],
    companiesNoPagination: [],
    companyTypes: [],
    liteSelectedCompany: null,
    selectedCompany: null,
    assignedOperators: null,
    nextPage: null,
    prevPage: null,
    count: null,
    sidebar: null,
  },
  reducers: {
    setCompaniesResult: (state, action) => {
      state.companies = action.payload.data;
      state.next = action.payload.pagination.next;
      state.previous = action.payload.pagination.previous;
      state.count = action.payload.pagination.count;
    },
    setCompanies: (state, action) => {
      state.companies = action.payload.data;
    },
    setCompaniesNoPagination: (state, action) => {
      state.companiesNoPagination = action.payload;
    },
    setCompanyTypes: (state, action) => {
      state.companyTypes = action.payload;
    },
    clearCompanies: (state) => {
      state.companies = [];
    },
    clearCompanyTypes: (state) => {
      state.companyTypes = [];
    },
    setSelectedCompany: (state, action) => {
      const omp  = action.payload.omp || {};
      state.selectedCompany = action.payload;
      state.liteSelectedCompany = {
        id: action.payload.id,
        name: action.payload.name,
      };
      state.sidebar = {
        id: action.payload.id,
        omp: {
          old_crm_contract_document: omp.old_crm_contract_document || null,
          old_crm_contract_url: omp.old_crm_contract_url || null,
          old_crm_shop_id: omp.old_crm_shop_id || null,
          old_crm_shop_link: omp.old_crm_shop_link || null,
        },
        client_first_name: action.payload.client_first_name,
        client_last_name: action.payload.client_last_name,
        name: action.payload.name,
        email: action.payload.email,
        phonenumber: action.payload.phonenumber,
        // Kept here too: the sidebar must not lose them when leaving Informazioni
        telegram_id: action.payload.telegram_id ?? null,
        facebook_page_id: action.payload.facebook_page_id ?? null,
        instagram_account_id: action.payload.instagram_account_id ?? null,
        logo: action.payload.logo ?? null,
        website: action.payload.website ?? "",
        managing_team: action.payload.managing_team,
        operators: action.payload.operators || [],
        contract_value: action.payload.contract_value,
        contract_start_date: action.payload.contract_start_date,
        contract_renew_date: action.payload.contract_renew_date,
        type: action.payload.type,
        status: action.payload.status,
        social_links: action.payload.social_links || [],
        stop_renew: action.payload.stop_renew || false,
        company_type_labels: action.payload.company_type_labels || [],
        company_type_labels_display: action.payload.company_type_labels_display || [],
        delivered_subtasks: action.payload.delivered_subtasks || [],
        assignments: action.payload.assignments || [],
      };
    },
    setLiteSelectedCompany: (state, action) => {
      state.liteSelectedCompany = action.payload;
    },
    setAssignedOperators: (state, action) => {
      state.assignedOperators = action.payload;
    },
    clearSelectedCompany: (state) => {
      state.selectedCompany = null;
    },
    clearLiteSelectedCompany: (state) => {
      state.liteSelectedCompany = null;
    },
    clearAssignedOperators: (state) => {
      state.assignedOperators = [];
    },
    setNextPage: (state, action) => {
      state.next = action.payload;
    },
    setPrevPage: (state, action) => {
      state.previous = action.payload;
    },
    setCount: (state, action) => {
      state.count = action.payload;
    },
    setSidebar: (state, action) => {
      state.sidebar = action.payload;
      state.liteSelectedCompany = {
        id: action.payload.id,
        name: action.payload.name,
      };
    },
    clearSidebar: (state) => {
      state.sidebar = null;
    },
    // Social links live in both copies: either may be missing (e.g. sidebar not loaded yet)
    setLinks: (state, action) => {
      linkCopies(state).forEach((copy) => {
        copy.social_links = action.payload;
      });
    },
    clearLinks: (state) => {
      linkCopies(state).forEach((copy) => {
        copy.social_links = [];
      });
    },
    addLink: (state, action) => {
      linkCopies(state).forEach((copy) => {
        copy.social_links = [...(copy.social_links || []), action.payload];
      });
    },
    updateLink: (state, action) => {
      linkCopies(state).forEach((copy) => {
        copy.social_links = (copy.social_links || []).map((link) =>
          link.id === action.payload.id ? action.payload : link
        );
      });
    },
    removeLink: (state, action) => {
      linkCopies(state).forEach((copy) => {
        copy.social_links = (copy.social_links || []).filter((link) => link.id !== action.payload);
      });
    },
    updateSelectedCompany: (state, action) => {
      const { id, data } = action.payload;

      state.companies = state.companies.map((c) =>
        c.id === id ? { ...c, ...data } : c
      );
      state.companiesNoPagination = state.companiesNoPagination.map((c) =>
        c.id === id ? { ...c, ...data } : c
      );

      if (state.selectedCompany?.id === id) {
        state.selectedCompany = { ...state.selectedCompany, ...data };
      }
      if (state.sidebar?.id === id) {
        state.sidebar = { ...state.sidebar, ...data };
      }
    },
  },
});
export const {
  setCompaniesResult,
  setCompanies,
  setCompaniesNoPagination,
  setCompanyTypes,
  clearCompanies,
  clearCompanyTypes,
  setSelectedCompany,
  updateSelectedCompany,
  setLiteSelectedCompany,
  clearSelectedCompany,
  clearLiteSelectedCompany,
  setAssignedOperators,
  clearAssignedOperators,
  setNextPage,
  setPrevPage,
  setCount,
  setSidebar,
  clearSidebar,
  setLinks,
  clearLinks,
  addLink,
  updateLink,
  removeLink,
} = companiesSlice.actions;
export default companiesSlice.reducer;
